"use client";
import React, { useCallback, useState } from "react";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useRouter } from "next/navigation";
import CheckoutPopup from "../checkout/checkout";

/**
 * Enhanced BuyNow component with consistent data structure
 */
const BuyNow = ({
  product,
  prepareProductData,
  isOutOfStock,
  customizations = {},
  trackPurchase,
  buttonText = "Buy Now",
  outOfStockText = "Out of Stock",
  className = "",
  usePopup = true,
}) => {
  const router = useRouter();
  const [showCheckoutPopup, setShowCheckoutPopup] = useState(false);
  const [checkoutProduct, setCheckoutProduct] = useState(null);

  // Enhanced product data normalization to match database structure
  const normalizeProductData = (productData) => {
    // Handle image arrays consistently
    let normalizedImages = [];

    if (productData.customImage) {
      normalizedImages = Array.isArray(productData.customImage)
        ? productData.customImage
        : [productData.customImage];
    } else if (productData.image) {
      normalizedImages = Array.isArray(productData.image)
        ? productData.image
        : [productData.image];
    } else if (
      productData.productImages &&
      Array.isArray(productData.productImages)
    ) {
      normalizedImages = productData.productImages;
    }

    // Ensure we have at least a placeholder
    if (normalizedImages.length === 0) {
      normalizedImages = ["/api/placeholder/100/100"];
    }

    // Extract price consistently
    const price =
      typeof productData.price === "number"
        ? productData.price
        : typeof productData.productPrice === "number"
        ? productData.productPrice
        : parseFloat(productData.price || productData.productPrice || "0");

    // Build variant string
    const variantParts = [];
    if (customizations.size) variantParts.push(customizations.size);
    if (customizations.color) variantParts.push(customizations.color);
    const variant = variantParts.join(" ");

    // Create comprehensive product object matching database structure
    return {
      // Essential identifiers
      id:
        productData.id ||
        `product_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,

      // Product names (maintain compatibility)
      name: productData.name || productData.productName || "Unknown Product",
      productName:
        productData.name || productData.productName || "Unknown Product",

      // Pricing
      price: price,
      productPrice: price,

      // Quantity
      quantity: productData.quantity || 1,

      // Images
      customImage: normalizedImages,
      image: normalizedImages[0], // First image for compatibility
      productImages: normalizedImages,

      // Categories
      Category: productData.Category || productData.category || "",
      category: productData.Category || productData.category || "",

      // Product details
      productType: productData.productType || "",
      productTag: productData.productTag || "",
      productDescription: productData.productDescription || "",

      // Variant information
      variant: variant || "",
      selectedSize: customizations.size || "",
      selectedColor: customizations.color || "",

      // Comprehensive customizations object
      customizations: {
        size: customizations.size || "",
        color: customizations.color || "",
        message: customizations.message || "",
        spotifyLink: customizations.spotifyLink || "",
        // Include any additional customization fields
        ...Object.keys(customizations).reduce((acc, key) => {
          if (!["size", "color", "message", "spotifyLink"].includes(key)) {
            acc[key] = customizations[key];
          }
          return acc;
        }, {}),
      },

      // Timestamps
      timestamp: Date.now(),
      addedAt: new Date().toISOString(),

      // Additional product fields that might be useful
      ...(productData.sku && { sku: productData.sku }),
      ...(productData.brand && { brand: productData.brand }),
      ...(productData.weight && { weight: productData.weight }),
      ...(productData.dimensions && { dimensions: productData.dimensions }),
      ...(productData.material && { material: productData.material }),
      ...(productData.tags && { tags: productData.tags }),
    };
  };

  // Validation helper
  const validateProductData = (productData) => {
    const errors = [];

    if (!productData.name && !productData.productName) {
      errors.push("Product name is missing");
    }

    if (!productData.price && !productData.productPrice) {
      errors.push("Product price is missing");
    }

    if (productData.price && isNaN(parseFloat(productData.price))) {
      errors.push("Product price is invalid");
    }

    return errors;
  };

  // Buy now handler with enhanced validation
  const handleBuyNow = useCallback(async () => {
    if (isOutOfStock) {
      toast.error(
        `This ${product?.productType || "product"} is currently out of stock`
      );
      return;
    }

    // Validate required customizations
    if (product?.Size?.length > 0 && !customizations.size) {
      toast.error("Please select a size");
      return;
    }

    if (product?.Color?.length > 0 && !customizations.color) {
      toast.error("Please select a color");
      return;
    }

    // Validate required custom fields
    if (product?.requiresMessage && !customizations.message?.trim()) {
      toast.error("Please add a message");
      return;
    }

    if (product?.requiresSpotifyLink && !customizations.spotifyLink?.trim()) {
      toast.error("Please add a Spotify link");
      return;
    }

    try {
      // Track the purchase event if tracking function is provided
      if (typeof trackPurchase === "function") {
        trackPurchase();
      }

      // Prepare product data for purchase
      const productData = await prepareProductData();
      if (!productData) {
        toast.error("Failed to prepare product data");
        return;
      }

      // Validate the product data
      const validationErrors = validateProductData(productData);
      if (validationErrors.length > 0) {
        console.error("Product validation errors:", validationErrors);
        toast.error("Product data is incomplete");
        return;
      }

      // Normalize the product data
      const normalizedProduct = normalizeProductData(productData);

      // Log for debugging (remove in production)
      console.log("Normalized product for checkout:", normalizedProduct);

      if (usePopup) {
        // Use popup checkout
        setCheckoutProduct([normalizedProduct]);
        setShowCheckoutPopup(true);
        toast.success("Opening checkout...");
      } else {
        // Store the normalized product in localStorage for checkout page
        const storageKey = "buyNowItems";
        localStorage.setItem(storageKey, JSON.stringify([normalizedProduct]));

        toast.success("Proceeding to checkout!");
        router.push("/checkout?source=buynow");
      }
    } catch (error) {
      console.error("Error processing buy now:", error);
      toast.error("Failed to process purchase. Please try again.");
    }
  }, [
    isOutOfStock,
    product,
    customizations,
    prepareProductData,
    trackPurchase,
    router,
    usePopup,
  ]);

  // Handle checkout popup close
  const handleCheckoutClose = () => {
    setShowCheckoutPopup(false);
    setCheckoutProduct(null);
  };

  return (
    <>
      <button
        onClick={handleBuyNow}
        disabled={isOutOfStock}
        className={`w-full py-2.5 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl 
                   font-semibold text-base md:text-lg transition-all duration-300 
                   flex items-center justify-center shadow-md hover:shadow-lg ${
                     isOutOfStock
                       ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                       : "bg-red-600 text-white hover:bg-red-700"
                   } ${className}`}
      >
        <CreditCard className="w-4 md:w-5 h-4 md:h-5 mr-2" />
        {isOutOfStock ? outOfStockText : buttonText}
      </button>

      {/* Checkout Popup - only render if using popup mode */}
      {usePopup && checkoutProduct && (
        <CheckoutPopup
          isOpen={showCheckoutPopup}
          onClose={handleCheckoutClose}
          cartItems={checkoutProduct}
          source="buynow"
        />
      )}
    </>
  );
};

export default BuyNow;
